import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEye, 
  faPen, 
  faPlus, 
  faTrash, 
  faToggleOn, 
  faToggleOff,
  faKey,
  faUsers,
  faUserCheck,
  faUserTimes,
  faLock,
  faLockOpen,
  faExclamationTriangle,
  faUnlock
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import Table from '../../../components/Table';
import Pagination from '../../../components/Pagination';
import UsuarioFilterBar from '../../../components/UsuarioFilterBar';
import ColumnVisibilityModal from '../../../components/ColumnVisibilityModal';
import ConfirmModal from '../../../components/ConfirmModal';
import { usuarioService } from '../../../services/usuarioService';
import { perfilService } from '../../../services/perfilService';
import { useAuth } from '../../../contexts/AuthContext';

// Função auxiliar para verificar se usuário está bloqueado
const isUserBlocked = (user) => {
  if (!user.bloqueado_ate) return false;
  return new Date() < new Date(user.bloqueado_ate);
};

// Colunas base (sem as que dependem de handlers)
const baseColumns = [
  { Header: 'ID', accessor: 'id' },
  { Header: 'Nome', accessor: 'nome' },
  { Header: 'Email', accessor: 'email' },
  { 
    Header: 'Perfil', 
    accessor: 'perfil_nome',
    Cell: (value) => (
      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
        {value}
      </span>
    )
  },
  { 
    Header: 'Status', 
    accessor: 'ativo',
    Cell: (value, row) => {
      const bloqueado = isUserBlocked(row);
      if (bloqueado) {
        return (
          <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs flex items-center gap-1">
            <FontAwesomeIcon icon={faLock} className="text-xs" />
            Bloqueado
          </span>
        );
      }
      return (
        <span className={`px-2 py-1 rounded-full text-xs ${
          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Ativo' : 'Inativo'}
        </span>
      );
    }
  },
  { 
    Header: 'Último Login', 
    accessor: 'ultimo_login',
    Cell: (value) => {
      if (!value) return 'Nunca';
      const date = new Date(value);
      const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
      return adjustedDate.toLocaleDateString('pt-BR');
    }
  },
  { 
    Header: 'Criado em', 
    accessor: 'created_at',
    Cell: (value) => {
      if (!value) return '';
      const date = new Date(value);
      const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
      return adjustedDate.toLocaleDateString('pt-BR');
    }
  }
];

const pageSizes = [5, 10, 20];

const UsuariosList = () => {
  const { hasPermission } = useAuth();
  
  const [filters, setFilters] = useState({ 
    nome: '', 
    email: '',
    perfil_id: '',
    ativo: '',
    search: ''
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizes[0]); // 5
  const [usuarios, setUsuarios] = useState([]);
  const [perfis, setPerfis] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(baseColumns.map(col => col.accessor).concat('acoes'));
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [showResetResultModal, setShowResetResultModal] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1
  });

  // Modal handlers
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleToggleColumn = (accessor) => {
    setVisibleColumns(prev =>
      prev.includes(accessor)
        ? prev.filter(col => col !== accessor)
        : [...prev, accessor]
    );
  };

  // Carregar dados
  useEffect(() => {
    loadUsuarios();
    loadPerfis();
    loadStats();
  }, [page, pageSize, filters]);

  const loadUsuarios = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: pageSize,
        ...filters
      };
      const response = await usuarioService.list(params);
      setUsuarios(response.data || []);
      setPagination({
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.totalPages || 0,
        currentPage: response.pagination?.page || 1
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPerfis = async () => {
    try {
      const response = await perfilService.list();
      setPerfis(response.perfis || []);
    } catch (err) {
      console.error('Erro ao carregar perfis:', err);
    }
  };

  const loadStats = async () => {
    try {
      const response = await usuarioService.getStats();
      setStats(response);
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    }
  };

  const handleDelete = async () => {
    try {
      await usuarioService.delete(selectedUser.id);
      setShowDeleteModal(false);
      setSelectedUser(null);
      loadUsuarios();
      loadStats();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleChangeStatus = async () => {
    try {
      await usuarioService.changeStatus(selectedUser.id, !selectedUser.ativo);
      setShowStatusModal(false);
      setSelectedUser(null);
      loadUsuarios();
      loadStats();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResetPassword = async () => {
    try {
      const result = await usuarioService.resetarSenha(selectedUser.id);
      setShowResetPasswordModal(false);
      setSelectedUser(null);
      // Mostrar modal com a senha temporária
      setResetResult(result.data);
      setShowResetResultModal(true);
    } catch (err) {
      setError(err.message);
      setShowResetPasswordModal(false);
      setSelectedUser(null);
    }
  };

  const handleUnlockUser = async () => {
    try {
      await usuarioService.desbloquear(selectedUser.id);
      setShowUnlockModal(false);
      setSelectedUser(null);
      loadUsuarios();
      loadStats();
    } catch (err) {
      setError(err.message);
      setShowUnlockModal(false);
      setSelectedUser(null);
    }
  };

  // Colunas com a coluna de ações dinâmica
  const columns = useMemo(() => {
    const actionColumn = {
      Header: 'Ações',
      accessor: 'acoes',
      Cell: (value, row) => {
        const bloqueado = isUserBlocked(row);
        return (
          <div className="flex items-center gap-1">
            {/* Visualizar */}
            <Link
              to={`/seguranca/usuarios/${row.id}`}
              className="text-blue-600 hover:text-blue-800 p-1.5 rounded hover:bg-blue-50"
              title="Visualizar"
            >
              <FontAwesomeIcon icon={faEye} />
            </Link>
            
            {/* Editar */}
            {hasPermission('usuarios.update') && (
              <Link
                to={`/seguranca/usuarios/${row.id}/editar`}
                className="text-green-600 hover:text-green-800 p-1.5 rounded hover:bg-green-50"
                title="Editar"
              >
                <FontAwesomeIcon icon={faPen} />
              </Link>
            )}
            
            {/* Desbloquear - só aparece se usuário está bloqueado */}
            {bloqueado && hasPermission('usuarios.update') && (
              <button
                onClick={() => {
                  setSelectedUser(row);
                  setShowUnlockModal(true);
                }}
                className="text-orange-600 hover:text-orange-800 p-1.5 rounded hover:bg-orange-50"
                title="Desbloquear usuário"
              >
                <FontAwesomeIcon icon={faUnlock} />
              </button>
            )}
            
            {/* Resetar Senha */}
            {hasPermission('usuarios.update') && (
              <button
                onClick={() => {
                  setSelectedUser(row);
                  setShowResetPasswordModal(true);
                }}
                className="text-purple-600 hover:text-purple-800 p-1.5 rounded hover:bg-purple-50"
                title="Resetar senha"
              >
                <FontAwesomeIcon icon={faKey} />
              </button>
            )}
            
            {/* Ativar/Desativar */}
            {hasPermission('usuarios.update') && (
              <button
                onClick={() => {
                  setSelectedUser(row);
                  setShowStatusModal(true);
                }}
                className={`p-1.5 rounded ${
                  row.ativo 
                    ? 'text-red-600 hover:text-red-800 hover:bg-red-50' 
                    : 'text-green-600 hover:text-green-800 hover:bg-green-50'
                }`}
                title={row.ativo ? 'Desativar usuário' : 'Ativar usuário'}
              >
                <FontAwesomeIcon icon={row.ativo ? faToggleOn : faToggleOff} />
              </button>
            )}
          </div>
        );
      }
    };

    return [...baseColumns, actionColumn];
  }, [hasPermission]);

  if (loading && !usuarios.length) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gerenciamento de Usuários</h1>
          <p className="text-gray-600">Gerencie usuários, perfis e permissões do sistema</p>
        </div>
        {hasPermission('usuarios.create') && (
          <Link
            to="/seguranca/usuarios/novo"
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faPlus} />
            Novo Usuário
          </Link>
        )}
      </div>

      {/* Estatísticas */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faUsers} className="text-2xl text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total de Usuários</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faUserCheck} className="text-2xl text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Usuários Ativos</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.ativos}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faUserTimes} className="text-2xl text-red-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Usuários Inativos</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.inativos}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faUsers} className="text-2xl text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Online Agora</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.online_agora || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <UsuarioFilterBar 
        filters={filters}
        setFilters={setFilters}
        onReset={() => {
          setPage(1);
          loadUsuarios();
        }}
      />

      {/* Erro */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Modal de Visibilidade de Colunas */}
      <ColumnVisibilityModal
        columns={columns}
        visibleColumns={visibleColumns}
        onChange={handleToggleColumn}
        onClose={handleCloseModal}
        isOpen={isModalOpen}
      />

      {/* Controles de Paginação e Colunas */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2 max-w-full">
        <div className="order-2 sm:order-1 w-full sm:w-auto">
          <Pagination 
            currentPage={pagination.currentPage} 
            totalPages={pagination.totalPages} 
            onPageChange={setPage} 
          />
        </div>
        <div className="order-1 sm:order-2 flex items-center w-full sm:w-auto justify-end gap-2">
          <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap mr-2">Por página:</label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            {pageSizes.map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
          <button
            type="button"
            className="flex items-center gap-2 px-3 py-1 rounded bg-green-600 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
            onClick={handleOpenModal}
            aria-label="Exibir/Ocultar colunas"
          >
            <FontAwesomeIcon icon={faEye} />
            <span>Colunas</span>
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg shadow">
        <Table
          columns={columns.filter(col => visibleColumns.includes(col.accessor))}
          data={usuarios}
          loading={loading}
        />
      </div>

      {/* Modal de Confirmação - Excluir */}
      <ConfirmModal
        isOpen={showDeleteModal}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir o usuário "${selectedUser?.nome}"? Esta ação não pode ser desfeita.`}
        onCancel={() => {
          setShowDeleteModal(false);
          setSelectedUser(null);
        }}
        onConfirm={handleDelete}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      {/* Modal de Confirmação - Status */}
      <ConfirmModal
        isOpen={showStatusModal}
        title={`${selectedUser?.ativo ? 'Desativar' : 'Ativar'} Usuário`}
        message={`Tem certeza que deseja ${selectedUser?.ativo ? 'desativar' : 'ativar'} o usuário "${selectedUser?.nome}"?`}
        onCancel={() => {
          setShowStatusModal(false);
          setSelectedUser(null);
        }}
        onConfirm={handleChangeStatus}
        confirmText={selectedUser?.ativo ? 'Desativar' : 'Ativar'}
        cancelText="Cancelar"
        type={selectedUser?.ativo ? 'danger' : 'success'}
      />

      {/* Modal de Confirmação - Reset Senha */}
      <ConfirmModal
        isOpen={showResetPasswordModal}
        title="Resetar Senha"
        message={`Tem certeza que deseja resetar a senha do usuário "${selectedUser?.nome}"? Uma senha temporária será gerada e o usuário deverá trocá-la no próximo login.`}
        onCancel={() => {
          setShowResetPasswordModal(false);
          setSelectedUser(null);
        }}
        onConfirm={handleResetPassword}
        confirmText="Resetar Senha"
        cancelText="Cancelar"
        type="warning"
      />

      {/* Modal de Confirmação - Desbloquear */}
      <ConfirmModal
        isOpen={showUnlockModal}
        title="Desbloquear Usuário"
        message={`Tem certeza que deseja desbloquear a conta do usuário "${selectedUser?.nome}"? Isso irá zerar as tentativas de login e permitir que o usuário acesse o sistema novamente.`}
        onCancel={() => {
          setShowUnlockModal(false);
          setSelectedUser(null);
        }}
        onConfirm={handleUnlockUser}
        confirmText="Desbloquear"
        cancelText="Cancelar"
        type="success"
      />

      {/* Modal - Resultado do Reset de Senha */}
      {showResetResultModal && resetResult && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => setShowResetResultModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6 transform transition-all">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-green-100">
                <FontAwesomeIcon icon={faKey} className="text-green-600 text-xl" />
              </div>
              <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
                Senha Resetada com Sucesso!
              </h3>
              <p className="text-sm text-gray-600 text-center mb-4">
                A senha do usuário <strong>{resetResult.nome}</strong> foi resetada.
              </p>
              
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-600 mb-2">Nova senha temporária:</p>
                <div className="flex items-center justify-between bg-white border rounded px-3 py-2">
                  <code className="text-lg font-mono font-bold text-indigo-600">
                    {resetResult.senha_temporaria}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(resetResult.senha_temporaria);
                      alert('Senha copiada para a área de transferência!');
                    }}
                    className="text-gray-500 hover:text-gray-700 p-1"
                    title="Copiar senha"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-500 mt-0.5" />
                  <div className="text-sm text-amber-700">
                    <strong>Importante:</strong> O usuário deverá trocar esta senha no próximo login.
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => {
                  setShowResetResultModal(false);
                  setResetResult(null);
                }}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsuariosList;
